/**
 * File Icons — lightweight extension-based colored file icons.
 *
 * Resolves a filename to a color + label, rendered via the shared
 * `ExtensionFileIcon` glyph (@/components/icons/ExtensionFileIcon).
 */
import type { ReactNode } from "react";
import { ExtensionFileIcon } from "@/components/icons/ExtensionFileIcon";
import { FilmFileIcon } from "@/components/icons/FilmFileIcon";

const EXT_CONFIG: Record<string, { color: string; label: string }> = {
  // PDF
  pdf: { color: "#ef4444", label: "PDF" },
  // Documents
  doc: { color: "#3b82f6", label: "DOC" },
  docx: { color: "#3b82f6", label: "DOC" },
  // Spreadsheets
  xls: { color: "#22c55e", label: "XLS" },
  xlsx: { color: "#22c55e", label: "XLS" },
  csv: { color: "#22c55e", label: "CSV" },
  // Presentations
  ppt: { color: "#f97316", label: "PPT" },
  pptx: { color: "#f97316", label: "PPT" },
  // Text
  txt: { color: "#6b7280", label: "TXT" },
  md: { color: "#6b7280", label: "MD" },
  // Data
  json: { color: "#a855f7", label: "JSON" },
  xml: { color: "#a855f7", label: "XML" },
  yaml: { color: "#a855f7", label: "YAML" },
  yml: { color: "#a855f7", label: "YML" },
};

const DEFAULT_CONFIG = { color: "#6b7280", label: "FILE" };

/** Video: a film strip, not a label (the file card's plate in the approved
 *  mockups/file-ask-card-2026-09-24.html). */
const VIDEO_EXTS = new Set(["mov", "mp4", "m4v", "webm", "mkv", "avi"]);
const VIDEO_COLOR = "#ec4899";

export function getFileIcon(filename: string, size = 20): ReactNode {
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  if (VIDEO_EXTS.has(ext)) {
    return <FilmFileIcon color={VIDEO_COLOR} width={size} height={size} />;
  }
  const config = EXT_CONFIG[ext] || DEFAULT_CONFIG;

  return (
    <ExtensionFileIcon
      color={config.color}
      label={config.label}
      width={size}
      height={size}
    />
  );
}

/** Get just the color for a filename extension */
export function getFileColor(filename: string): string {
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  if (VIDEO_EXTS.has(ext)) return VIDEO_COLOR;
  return (EXT_CONFIG[ext] || DEFAULT_CONFIG).color;
}
